import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Search, X, Map, List as ListIcon, SlidersHorizontal } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import ListingCard from "@/components/ListingCard";
import EmptyState from "@/components/EmptyState";
import MapboxListingsMap from "@/components/MapboxListingsMap";
import { useI18n } from "@/contexts/I18nContext";
import api from "@/lib/api";
import { SPACE_CATEGORY_LABELS } from "@/lib/offer";
import { AUTO_CATEGORIES, CATEGORY_LABELS, REAL_ESTATE_CATEGORIES } from "@/lib/catalog";
import { STAY_CATEGORIES } from "@/lib/hospitality";
import { COUNTRIES } from "@/data/locations";
import Seo from "@/components/Seo";

const RE_CATS = Object.keys(REAL_ESTATE_CATEGORIES);
const AUTO_CATS = Object.keys(AUTO_CATEGORIES);
const STAY_CATS = Object.keys(STAY_CATEGORIES);
const SPACE_CATEGORIES = ["coworking", "bureau_prive", "salle_reunion", "salle_conference", "salle_formation", "salle_mariage", "salle_reception"];

export default function Explore() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const sector = params.get("sector") || "";
  const portal = params.get("portal") || "";
  const purpose = params.get("purpose") || "";
  const category = params.get("category") || "";
  const country = params.get("country") || "";
  const city = params.get("city") || "";
  const q = params.get("q") || "";
  const minP = params.get("min_price") || "";
  const maxP = params.get("max_price") || "";
  const period = params.get("price_period") || "";
  const furnished = params.get("furnished") || "";
  const minCapacity = params.get("min_capacity") || "";
  const minBedrooms = params.get("min_bedrooms") || "";
  const minBathrooms = params.get("min_bathrooms") || "";
  const minSurface = params.get("min_surface") || "";
  const brand = params.get("brand") || "";
  const model = params.get("model") || "";
  const minYear = params.get("min_year") || "";
  const maxYear = params.get("max_year") || "";
  const transmission = params.get("transmission") || "";
  const fuel = params.get("fuel") || "";

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v == null || v === "") next.delete(k);
    else next.set(k, v);
    setParams(next, { replace: true });
  };
  const reset = () => {
    const next = new URLSearchParams();
    if (sector) next.set("sector", sector);
    if (portal) next.set("portal", portal);
    if (purpose) next.set("purpose", purpose);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    setLoading(true);
    const clean = {};
    ["sector", "purpose", "category", "country", "city", "q", "min_price", "max_price", "price_period", "furnished", "min_capacity", "min_bedrooms", "min_bathrooms", "min_surface", "brand", "model", "min_year", "max_year", "transmission", "fuel"].forEach((k) => {
      const v = params.get(k);
      if (v) clean[k] = v;
    });
    api.get("/listings", { params: clean })
      .then((r) => {
        const rows = Array.isArray(r.data) ? r.data : [];
        setListings(portal === "espaces" ? rows.filter((item) => SPACE_CATEGORIES.includes(item.category)) : rows);
      })
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  }, [params, portal]);

  const isSpacesPortal = portal === "espaces";
  const cats = isSpacesPortal ? SPACE_CATEGORIES : sector === "auto" ? AUTO_CATS : sector === "real_estate" ? RE_CATS.filter((key) => !SPACE_CATEGORIES.includes(key)) : sector === "hospitality" ? STAY_CATS : [];
  const portalSlug = sector === "auto" ? "automobile" : sector === "hospitality" ? "sejours" : isSpacesPortal ? "espaces" : "immobilier";
  const accent = sector === "auto" ? "#0866f5" : sector === "hospitality" || SPACE_CATEGORIES.includes(category) ? "#5b32d6" : "#fe6c09";
  const seoNames = {
    immobilier: ["Immobilier", "Explorez les biens immobiliers disponibles avec Team Work Africa."],
    automobile: ["Automobile", "Explorez les véhicules disponibles à la vente ou à la location avec Team Work Africa."],
    sejours: ["Hôtels & séjours", "Explorez les hôtels, appartements meublés, studios et résidences disponibles avec Team Work Africa."],
    espaces: ["Espaces & événements", "Explorez coworking, bureaux et salles pour réunions, conférences, mariages et réceptions avec Team Work Africa."],
  };
  const [seoName, seoDescription] = seoNames[portalSlug] || ["Explorer les offres", "Explorez les offres disponibles sur Team Work Africa."];
  const hasFilterQuery = [...params.keys()].some((key) => !["sector", "portal"].includes(key));

  const activeFilters = useMemo(() => [
    purpose && { k: "purpose", label: purpose === "sale" ? "Vente" : "Location" },
    category && { k: "category", label: CATEGORY_LABELS[category] || category.replaceAll("_", " ") },
    country && { k: "country", label: COUNTRIES.find((item) => item.code === country)?.name || country },
    city && { k: "city", label: city },
    q && { k: "q", label: q },
  ].filter(Boolean), [purpose, category, country, city, q]);

  return (
    <>
      <Seo
        title={`${seoName} — Explorer les offres | Team Work Africa`}
        description={seoDescription}
        path="/explorer"
        noindex={hasFilterQuery}
        schema={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${seoName} — Explorer les offres`,
          url: "https://teamwork-africa.com/explorer",
          description: seoDescription,
          isPartOf: { "@id": "https://teamwork-africa.com/#website" },
        }}
      />
      <div className="min-h-screen bg-[#f7f8fa] pb-24">
      <header className="sticky top-0 z-20 border-b border-slate-100 bg-white/95 px-4 pb-3 pt-4 backdrop-blur lg:hidden">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(`/univers/${portalSlug}`)} className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100" aria-label="Retour au portail"><ArrowLeft className="h-5 w-5" /></button>
          <div className="text-center"><h1 className="font-display text-base font-extrabold text-brand-navy">Résultats</h1><p className="text-[10px] text-slate-500">{listings.length} offre{listings.length > 1 ? "s" : ""}</p></div>
          <button onClick={() => setFiltersOpen(true)} className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100" aria-label="Ouvrir les filtres"><SlidersHorizontal className="h-5 w-5" /></button>
        </div>
        <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
          {sector !== "hospitality" && !isSpacesPortal && <><button onClick={() => setParam("purpose", "sale")} className="h-8 shrink-0 rounded-full px-3 text-xs font-bold" style={{ backgroundColor: purpose === "sale" ? accent : "#f1f5f9", color: purpose === "sale" ? "white" : "#0e253a" }}>Vente</button><button onClick={() => setParam("purpose", "rent")} className="h-8 shrink-0 rounded-full px-3 text-xs font-bold" style={{ backgroundColor: purpose === "rent" ? accent : "#f1f5f9", color: purpose === "rent" ? "white" : "#0e253a" }}>Location</button></>}
          {cats.slice(0, 5).map((cat) => <button key={cat} onClick={() => setParam("category", cat)} className="h-8 shrink-0 rounded-full px-3 text-xs font-bold" style={{ backgroundColor: category === cat ? accent : "#f1f5f9", color: category === cat ? "white" : "#0e253a" }}>{CATEGORY_LABELS[cat] || cat.replaceAll("_", " ")}</button>)}
        </div>
        {activeFilters.length > 0 && <div className="mt-3 flex items-center gap-2 overflow-x-auto no-scrollbar"><span className="shrink-0 text-[10px] font-bold text-slate-500">Filtres actifs</span>{activeFilters.map((filter) => <button key={filter.k} onClick={() => setParam(filter.k, "")} className="inline-flex h-7 shrink-0 items-center gap-1 rounded-full bg-slate-100 px-2.5 text-[10px] font-semibold text-slate-600">{filter.label}<X className="h-3 w-3" /></button>)}<button onClick={reset} className="shrink-0 text-[10px] font-bold" style={{ color: accent }}>Réinitialiser</button></div>}
      </header>

      <div className="container-app py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-slate-500" data-testid="listing-count">
            <span className="font-bold text-brand-navy">{listings.length}</span> {t("filters.count")}
          </div>
          <div className="flex items-center gap-2"><button onClick={() => setFiltersOpen(true)} className="hidden h-9 items-center gap-2 rounded-full border border-slate-200 bg-white px-3 text-xs font-bold text-brand-navy lg:inline-flex"><SlidersHorizontal className="h-4 w-4" />Filtres</button><div className="inline-flex bg-brand-mist rounded-full p-1">
            <button
              onClick={() => setView("list")}
              className={`px-3 h-8 rounded-full text-xs font-semibold flex items-center gap-1 ${view === "list" ? "bg-white text-brand-navy shadow-sm" : "text-slate-500"}`}
              data-testid="view-list"
            >
              <ListIcon className="h-3.5 w-3.5" /> {t("filters.list")}
            </button>
            <button
              onClick={() => setView("map")}
              className={`px-3 h-8 rounded-full text-xs font-semibold flex items-center gap-1 ${view === "map" ? "bg-white text-brand-navy shadow-sm" : "text-slate-500"}`}
              data-testid="view-map"
            >
              <Map className="h-3.5 w-3.5" /> {t("filters.map")}
            </button>
          </div></div>
        </div>

        {view === "map" ? (
          <MapboxListingsMap listings={listings} />
        ) : loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[4/3] rounded-2xl bg-brand-mist animate-pulse" />
            ))}
          </div>
        ) : listings.length === 0 ? (
          <EmptyState
            icon={Search}
            title="Aucune annonce ne correspond à vos critères."
            text="Essayez d'élargir votre recherche ou décrivez votre projet à un conseiller."
            action={<div className="flex flex-wrap justify-center gap-2"><Button variant="outline" onClick={reset}>{t("filters.reset")}</Button><Button asChild className="btn-primary"><Link to={`/demandes/nouvelle?sector=${isSpacesPortal ? "professional" : sector === "real_estate" ? "immobilier" : sector || "immobilier"}${isSpacesPortal && category ? `&space=${category}` : category ? `&category=${category}` : ""}${purpose ? `&transaction=${purpose === "sale" ? "buy" : "rent"}` : ""}&context=1`}>Trouver pour moi</Link></Button></div>}
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-testid="listings-grid">
            {listings.map((l) => <ListingCard key={l.id} listing={l} compact />)}
          </div>
        )}
      </div>

      {/* Filters sheet */}
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh] overflow-y-auto" data-testid="filters-sheet">
          <SheetHeader>
            <SheetTitle className="font-display text-xl text-brand-navy">Filtres avancés</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-4 pb-6">
            <div>
              <Label>Pays</Label>
              <select value={country} onChange={(e) => setParam("country", e.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm" data-testid="filter-country"><option value="">Tous les pays</option>{COUNTRIES.map((item) => <option key={item.code} value={item.code}>{item.name}</option>)}</select>
            </div>
            <div>
              <Label>{t("filters.city")}</Label>
              <Input value={city} onChange={(e) => setParam("city", e.target.value)} placeholder="Ex : Kinshasa" data-testid="filter-city" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("filters.minPrice")}</Label>
                <Input type="number" value={minP} onChange={(e) => setParam("min_price", e.target.value)} data-testid="filter-min-price" />
              </div>
              <div>
                <Label>{t("filters.maxPrice")}</Label>
                <Input type="number" value={maxP} onChange={(e) => setParam("max_price", e.target.value)} data-testid="filter-max-price" />
              </div>
            </div>
            {sector === "real_estate" && purpose === "rent" && <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label>Tarification</Label><select value={period} onChange={(e) => setParam("price_period", e.target.value)} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm"><option value="">Toutes</option><option value="hour">Par heure</option><option value="day">Par jour</option><option value="week">Par semaine</option><option value="month">Par mois</option><option value="event">Par événement</option></select></div>
              <div><Label>Ameublement</Label><select value={furnished} onChange={(e) => setParam("furnished", e.target.value)} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm"><option value="">Tous</option><option value="meuble">Meublé</option><option value="semi_meuble">Semi-meublé</option><option value="non_meuble">Non meublé</option></select></div>
              <div><Label>Capacité minimum</Label><Input type="number" value={minCapacity} onChange={(e) => setParam("min_capacity", e.target.value)} placeholder="Nombre de personnes"/></div>
            </div>}
            {sector === "hospitality" && <div className="grid grid-cols-1 sm:grid-cols-3 gap-3"><div><Label>Tarification</Label><select value={period} onChange={(e) => setParam("price_period", e.target.value)} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm"><option value="">Toutes</option><option value="day">Par nuit</option><option value="week">Par semaine</option><option value="month">Par mois</option></select></div><div><Label>Voyageurs minimum</Label><Input type="number" value={minCapacity} onChange={(e) => setParam("min_capacity", e.target.value)} /></div><div><Label>Chambres minimum</Label><Input type="number" value={minBedrooms} onChange={(e) => setParam("min_bedrooms", e.target.value)} /></div></div>}
            {sector === "real_estate" && <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label>Chambres minimum</Label><Input type="number" value={minBedrooms} onChange={(e) => setParam("min_bedrooms", e.target.value)} /></div>
              <div><Label>Salles de bain minimum</Label><Input type="number" value={minBathrooms} onChange={(e) => setParam("min_bathrooms", e.target.value)} /></div>
              <div><Label>Surface minimum (m²)</Label><Input type="number" value={minSurface} onChange={(e) => setParam("min_surface", e.target.value)} /></div>
            </div>}
            {sector === "auto" && <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label>Marque</Label><Input value={brand} onChange={(e) => setParam("brand", e.target.value)} placeholder="Toyota" /></div>
              <div><Label>Modèle</Label><Input value={model} onChange={(e) => setParam("model", e.target.value)} placeholder="Land Cruiser" /></div>
              <div><Label>Année minimum</Label><Input type="number" value={minYear} onChange={(e) => setParam("min_year", e.target.value)} /></div>
              <div><Label>Année maximum</Label><Input type="number" value={maxYear} onChange={(e) => setParam("max_year", e.target.value)} /></div>
              <div><Label>Transmission</Label><select value={transmission} onChange={(e) => setParam("transmission", e.target.value)} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm"><option value="">Toutes</option><option value="manuelle">Manuelle</option><option value="automatique">Automatique</option><option value="semi_auto">Semi-automatique</option></select></div>
              <div><Label>Carburant</Label><select value={fuel} onChange={(e) => setParam("fuel", e.target.value)} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm"><option value="">Tous</option><option value="essence">Essence</option><option value="diesel">Diesel</option><option value="hybride">Hybride</option><option value="electrique">Électrique</option></select></div>
            </div>}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={reset} data-testid="filters-reset">{t("filters.reset")}</Button>
              <Button className="btn-primary flex-1" onClick={() => setFiltersOpen(false)} data-testid="filters-apply">{t("filters.apply")}</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
    </>
  );
}
